'use client';

import { createContext, useContext, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { organisationApi } from '@/api/organisation';
import {
  OnboardingData,
  Organisation,
  Employee,
  InviteEmployeeData,
  DashboardStats,
  AnalyticsData,
} from '@/types/organisation';
import type {
  RecentNotificationsData,
  RecentNotificationsResponse,
  NotificationItem,
} from '@/types/notifications';
import { useToast } from '@/hooks/use-toast';
import { isBackendUnavailableError } from '@/lib/backend-maintenance';
import { ROUTES } from '@/lib/constants';
import type { EmployeeActivityReportPayload, EmployeeActivityReportSuccessData } from '@/types/reports';

interface OrganisationState {
  data: Organisation | null;
  isLoading: boolean;
  error: string | null;
}

interface EmployeesState {
  data: Employee[];
  isLoading: boolean;
  error: string | null;
}

interface RecentNotificationsState {
  data: RecentNotificationsData | null;
  isLoading: boolean;
  error: string | null;
}

interface OrganisationContextType {
  organisation: OrganisationState;
  employees: EmployeesState;
  isOnboardingRequired: boolean;
  completeOnboarding: (data: OnboardingData) => Promise<void>;
  updateOrganisation: (options: UpdateOrganisationOptions) => Promise<void>;
  setOrganisation: (organisation: Organisation | null) => void;
  fetchOrganisation: () => Promise<void>;
  fetchEmployees: () => Promise<void>;
  inviteEmployee: (data: InviteEmployeeData) => Promise<void>;
  removeEmployee: (employeeId: string) => Promise<void>;
  stats: {
    data: DashboardStats | null;
    isLoading: boolean;
    error: string | null;
  };
  fetchDashboardStats: () => Promise<void>;
  recentNotifications: RecentNotificationsState;
  fetchRecentNotifications: () => Promise<void>;
  generateEmployeeActivityReport: (payload?: EmployeeActivityReportPayload) => Promise<boolean>;
  employeeActivityReport: EmployeeActivityReportSuccessData | null;
  isGeneratingReport: boolean;
  generateReportError: string | null;
  analytics: {
    data: AnalyticsData | null;
    isLoading: boolean;
    error: string | null;
  };
  fetchAnalytics: (startDate?: string, endDate?: string) => Promise<void>;
}

interface UpdateOrganisationOptions {
  data: Partial<Organisation>;
  logo?: File;
}

const OrganisationContext = createContext<OrganisationContextType | undefined>(undefined);

export function OrganisationProvider({ children }: { children: React.ReactNode }) {
  const [organisation, setOrganisationState] = useState<OrganisationState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [employees, setEmployeesState] = useState<EmployeesState>({
    data: [],
    isLoading: false,
    error: null,
  });

  const [isOnboardingRequired, setIsOnboardingRequired] = useState(false);
  const { toast } = useToast();
  const router = useRouter();

  const [stats, setStats] = useState<{
    data: DashboardStats | null;
    isLoading: boolean;
    error: string | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [recentNotifications, setRecentNotificationsState] = useState<RecentNotificationsState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [employeeActivityReport, setEmployeeActivityReport] = useState<EmployeeActivityReportSuccessData | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [generateReportError, setGenerateReportError] = useState<string | null>(null);

  const [analytics, setAnalyticsState] = useState<{
    data: AnalyticsData | null;
    isLoading: boolean;
    error: string | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
  });

  const setOrganisation = useCallback((data: Organisation | null) => {
    setOrganisationState(prev => ({ ...prev, data }));
  }, []);

  const fetchOrganisation = useCallback(async () => {
    setOrganisationState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await organisationApi.getOrganisation();

      setOrganisationState({
        data: response.organisation,
        isLoading: false,
        error: null,
      });
      setIsOnboardingRequired(!response.organisation?.isOnboardingDone);
    } catch (error) {
      setOrganisationState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch organisation data',
      }));
      throw error;
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    setEmployeesState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await organisationApi.getEmployees();

      setEmployeesState({
        data: response.employees,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setEmployeesState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch employees',
      }));
    }
  }, []);

  const inviteEmployee = useCallback(
    async (data: InviteEmployeeData) => {
      try {
        await organisationApi.inviteEmployee(data);
        await fetchEmployees();
        toast({
          title: 'Success',
          description: 'Employee invited successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to invite employee',
          variant: 'error',
        });
        throw error;
      }
    },
    [fetchEmployees, toast]
  );

  const removeEmployee = useCallback(
    async (employeeId: string) => {
      try {
        await organisationApi.removeEmployee(employeeId);
        await fetchEmployees();
        toast({
          title: 'Success',
          description: 'Employee removed successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to remove employee',
          variant: 'error',
        });
        throw error;
      }
    },
    [fetchEmployees, toast]
  );

  const completeOnboarding = useCallback(
    async (data: OnboardingData) => {
      setOrganisationState(prev => ({ ...prev, isLoading: true }));
      try {
        const { organisation: newOrg } = await organisationApi.completeOnboarding(data);
        setOrganisationState({
          data: newOrg,
          isLoading: false,
          error: null,
        });
        setIsOnboardingRequired(false);

        toast({
          title: 'Success',
          description: 'Organisation setup completed successfully',
        });

        router.replace(ROUTES.DASHBOARD);
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to complete setup',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast, router]
  );

  const updateOrganisation = useCallback(
    async ({ data, logo }: UpdateOrganisationOptions) => {
      if (!organisation.data) {
        throw new Error('No organization data available');
      }

      setOrganisationState(prev => ({ ...prev, isLoading: true }));
      try {
        const { organisation: updatedOrg } = await organisationApi.update(data, logo);
        setOrganisationState({
          data: updatedOrg,
          isLoading: false,
          error: null,
        });
        toast({
          title: 'Success',
          description: 'Organisation updated successfully',
        });
      } catch (error) {
        setOrganisationState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to update organisation',
        }));
        throw error;
      }
    },
    [organisation.data, toast]
  );

  const fetchDashboardStats = useCallback(async () => {
    setStats(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await organisationApi.getDashboardStats();
      const next: DashboardStats = {
        totalApplicants: response.stats.totalApplicants,
        openJobs: response.stats.openJobs,
        totalAIInterviewCount: response.stats.totalAIInterviewCount,
        totalCandidates: response.stats.totalCandidates,
      };
      setStats({
        data: next,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setStats(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch stats',
      }));
      if (!isBackendUnavailableError(error)) {
        toast({
          description: 'Failed to fetch dashboard stats',
          variant: 'error',
        });
      }
    }
  }, [toast]);

  const fetchRecentNotifications = useCallback(async () => {
    setRecentNotificationsState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await organisationApi.getRecentNotifications();
      // handleAPIResponse already unwraps .data, so response is RecentNotificationsData directly
      setRecentNotificationsState({
        data: response,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch recent notifications';
      setRecentNotificationsState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, []);

  const generateEmployeeActivityReport = useCallback(async (payload?: EmployeeActivityReportPayload): Promise<boolean> => {
    setIsGeneratingReport(true);
    setGenerateReportError(null);
    try {
      const reportData = await organisationApi.generateEmployeeActivityReport(payload);
      setEmployeeActivityReport(reportData); // Store the received data (CSV string and filename)

      // Trigger download
      const blob = new Blob([reportData.csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', reportData.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // toast({ title: 'Report Downloaded', description: `Successfully downloaded ${reportData.filename}.` }); // Example toast
      return true; // Indicate download was initiated
    } catch (error: any) {
      console.error('Error generating employee activity report:', error);
      setGenerateReportError(error.message || 'An unexpected error occurred while generating the report.');
      setEmployeeActivityReport(null); // Clear report data on error
      return false; // Indicate failure
    } finally {
      setIsGeneratingReport(false);
    }
  }, [setEmployeeActivityReport, setIsGeneratingReport, setGenerateReportError /*, toast */]); // Add toast if used

  const fetchAnalytics = useCallback(async (startDate?: string, endDate?: string) => {
    setAnalyticsState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await organisationApi.getAnalytics(startDate, endDate);
      setAnalyticsState({
        data: response,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setAnalyticsState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch analytics',
      }));
      toast({
        description: 'Failed to fetch analytics',
        variant: 'error',
      });
    }
  }, [toast]);

  return (
    <OrganisationContext.Provider
      value={{
        organisation,
        employees,
        isOnboardingRequired,
        completeOnboarding,
        updateOrganisation,
        setOrganisation,
        fetchOrganisation,
        fetchEmployees,
        inviteEmployee,
        removeEmployee,
        stats,
        fetchDashboardStats,
        recentNotifications,
        fetchRecentNotifications,
        generateEmployeeActivityReport,
        employeeActivityReport,
        isGeneratingReport,
        generateReportError,
        analytics,
        fetchAnalytics,
      }}
    >
      {children}
    </OrganisationContext.Provider>
  );
}

export const useOrganisation = () => {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error('useOrganisation must be used within an OrganisationProvider');
  }
  return context;
};
